import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaLock, FaMusic, FaStickyNote } from 'react-icons/fa';
import { useUserPrefs } from '../UserPrefsProvider';
import { useAuth } from '../AuthProvider';
import { collection, setDoc, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { SAMPLE_SETS } from '../data/sampleSets';
import { getUserProfile, getPublicSets } from '../utils/socialFirestore';

const SAMPLE_ACCOUNT_USERNAME = 'allmytunes';

const GENRE_OPTIONS = ['Irish', 'Old-Time', 'Bluegrass', 'Klezmer'];

const overlay = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.78)',
  zIndex: 1100,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '20px', boxSizing: 'border-box',
};

const card = {
  backgroundColor: 'var(--bg-card, #fff)',
  color: 'var(--text-primary, #333)',
  borderRadius: '18px',
  maxWidth: '460px',
  width: '100%',
  padding: '32px 28px 24px',
  textAlign: 'center',
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
};

const btnPrimary = {
  padding: '10px 26px',
  borderRadius: '10px',
  border: 'none',
  backgroundColor: 'var(--text-link, #228be6)',
  color: '#fff',
  fontSize: '0.95rem',
  fontWeight: 700,
  cursor: 'pointer',
};

const btnSecondary = {
  padding: '9px 18px',
  borderRadius: '10px',
  border: '1px solid var(--border-color, #ccc)',
  backgroundColor: 'transparent',
  color: 'var(--text-primary, #333)',
  fontSize: '0.9rem',
  cursor: 'pointer',
};

const btnSkip = {
  background: 'none', border: 'none',
  color: 'var(--text-muted, #999)',
  fontSize: '0.82rem', cursor: 'pointer',
  padding: '4px 8px',
};

function Dots({ steps, current }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: '7px', marginTop: '1.4rem' }}>
      {steps.map((_, i) => (
        <div key={i} style={{
          width: '7px', height: '7px', borderRadius: '50%',
          backgroundColor: i === current ? 'var(--text-link, #228be6)' : 'var(--border-color, #ccc)',
          transition: 'background-color 0.2s',
        }} />
      ))}
    </div>
  );
}

// ─── Step 0: Welcome ──────────────────────────────────────────────────
function WelcomeStep({ onNext, onSkip }) {
  return (
    <>
      <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🎵</div>
      <h2 style={{ fontSize: '1.4em', margin: '0 0 0.8rem', fontWeight: 800 }}>
        Welcome to AllMyTunes
      </h2>
      <p style={{ margin: '0 0 0.6rem', lineHeight: 1.6, opacity: 0.9 }}>
        Your personal music library — store repertoire, build sets, track sessions, and keep notes, all in one place.
      </p>
      <p style={{ margin: '0 0 1.6rem', lineHeight: 1.6, opacity: 0.75, fontSize: '0.9rem' }}>
        Works offline, syncs across all your devices.
      </p>
      <button style={btnPrimary} onClick={onNext}>
        Get Started →
      </button>
      <div style={{ marginTop: '0.9rem' }}>
        <button style={btnSkip} onClick={onSkip}>Skip setup</button>
      </div>
    </>
  );
}

// ─── Step 1: Mode ─────────────────────────────────────────────────────
function ModeStep({ mode, setMode, onNext, onBack, onSkip }) {
  const modeCard = (id, icon, title, desc, color) => {
    const isSelected = mode === id;
    const isOther = mode !== 'all' && mode !== id;
    return (
      <div
        onClick={() => setMode(id)}
        style={{
          flex: '1 1 0', minWidth: 0,
          padding: '14px 12px 12px',
          borderRadius: '12px',
          border: `2px solid ${isSelected ? color : 'var(--border-color, #ccc)'}`,
          background: isSelected ? `color-mix(in srgb, ${color} 14%, transparent)` : 'transparent',
          cursor: 'pointer',
          opacity: isOther ? 0.42 : 1,
          transition: 'opacity 0.2s, border-color 0.2s',
          position: 'relative',
        }}
      >
        {isOther && (
          <div style={{
            position: 'absolute', top: '6px', right: '8px',
            fontSize: '0.7rem', color: 'var(--text-muted)',
          }}>
            <FaLock /> unlock later
          </div>
        )}
        <div style={{ fontSize: '1.6rem', marginBottom: '0.3rem' }}>{icon}</div>
        <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{title}</div>
        <div style={{ fontSize: '0.8rem', opacity: 0.75, lineHeight: 1.4 }}>{desc}</div>
      </div>
    );
  };

  return (
    <>
      <h2 style={{ fontSize: '1.2em', margin: '0 0 0.5rem', fontWeight: 800 }}>
        What will you mainly use this for?
      </h2>
      <p style={{ margin: '0 0 1.1rem', fontSize: '0.88rem', opacity: 0.7 }}>
        You can always change this in Settings.
      </p>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '0.8rem' }}>
        {modeCard('tunes', <FaMusic />, 'Tunes', 'Repertoire, sets, sessions, ABC notation', '#228be6')}
        {modeCard('notes', <FaStickyNote />, 'Notes', 'Ideas, tasks, links, references', '#d97706')}
      </div>

      {/* All-mode option disabled — uncomment to restore
      <button
        onClick={() => setMode('all')}
        style={{
          width: '100%', padding: '8px 12px', borderRadius: '10px',
          border: `1px solid ${mode === 'all' ? '#5c7cfa' : 'var(--border-color, #ccc)'}`,
          background: mode === 'all' ? 'color-mix(in srgb, #5c7cfa 15%, transparent)' : 'transparent',
          color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.88rem',
          fontWeight: mode === 'all' ? 700 : 400,
          marginBottom: '1.2rem',
        }}
      >
        Both — use Tunes and Notes together
      </button>
      */}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button style={btnSkip} onClick={onSkip}>Skip setup</button>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={btnSecondary} onClick={onBack}>Back</button>
          <button style={btnPrimary} onClick={onNext}>Next</button>
        </div>
      </div>
    </>
  );
}

// ─── Step 2: Genres ───────────────────────────────────────────────────
function GenresStep({ selected, setSelected, otherGenre, setOtherGenre, onNext, onBack, onSkip }) {
  const toggle = (g) => setSelected(prev =>
    prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]
  );
  const showOther = selected.includes('Other');

  return (
    <>
      <h2 style={{ fontSize: '1.2em', margin: '0 0 0.4rem', fontWeight: 800 }}>
        Which genres do you play?
      </h2>
      <p style={{ margin: '0 0 1rem', fontSize: '0.88rem', opacity: 0.7 }}>
        We'll add these as genre options across the app.
      </p>

      <div style={{ textAlign: 'left', display: 'grid', gap: '8px', marginBottom: '1.1rem' }}>
        {[...GENRE_OPTIONS, 'Other'].map(g => (
          <label key={g} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '7px 10px', borderRadius: '8px', background: selected.includes(g) ? 'color-mix(in srgb, var(--text-link, #228be6) 10%, transparent)' : 'transparent', border: `1px solid ${selected.includes(g) ? 'color-mix(in srgb, var(--text-link, #228be6) 40%, var(--border-color) 60%)' : 'var(--border-color, #ccc)'}` }}>
            <input
              type="checkbox"
              checked={selected.includes(g)}
              onChange={() => toggle(g)}
              style={{ width: '17px', height: '17px', cursor: 'pointer', accentColor: 'var(--text-link, #228be6)' }}
            />
            <span style={{ fontWeight: selected.includes(g) ? 600 : 400 }}>{g}</span>
          </label>
        ))}
        {showOther && (
          <input
            type="text"
            value={otherGenre}
            onChange={e => setOtherGenre(e.target.value)}
            placeholder="Your genre (e.g. Cajun, Bluegrass)"
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '8px 12px', borderRadius: '8px',
              border: '1px solid var(--border-color, #ccc)',
              background: 'var(--bg-card)', color: 'var(--text-primary)',
              fontSize: '0.9rem',
            }}
          />
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button style={btnSkip} onClick={onSkip}>Skip setup</button>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={btnSecondary} onClick={onBack}>Back</button>
          <button
            style={{ ...btnPrimary, opacity: (selected.filter(g => g !== 'Other').length === 0 && !otherGenre.trim()) ? 0.5 : 1 }}
            onClick={onNext}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Step 2b: Sample Sets ─────────────────────────────────────────────
function SamplesStep({ selectedGenres, onNext, onBack, onSkip }) {
  const { user } = useAuth();
  const [sets, setSets] = useState([]);
  const [sourceUid, setSourceUid] = useState(null); // non-null = Firestore source
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const usernameSnap = await getDoc(doc(db, 'usernames', SAMPLE_ACCOUNT_USERNAME));
        if (usernameSnap.exists()) {
          const uid = usernameSnap.data().uid;
          const profile = await getUserProfile(uid);
          const publicSets = await getPublicSets(uid, profile);
          const all = publicSets.map(s => ({ ...s, _sourceUid: uid }));
          if (all.length > 0) {
            setSets(all);
            setSourceUid(uid);
            setSelectedIds([]);
            setLoading(false);
            return;
          }
        }
      } catch (_) {}
      // Fall back to static data
      const filtered = SAMPLE_SETS.filter(s =>
        selectedGenres.length === 0 ||
        selectedGenres.some(g => s.genre?.toLowerCase() === g.toLowerCase())
      );
      setSets(filtered);
      setSelectedIds(filtered.map(s => s.id));
      setLoading(false);
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-advance if nothing to show
  useEffect(() => {
    if (!loading && sets.length === 0) onNext();
  }, [loading, sets.length, onNext]);

  const toggleSet = (id) => setSelectedIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const handleImport = async () => {
    if (!user || selectedIds.length === 0) { onNext(); return; }
    setImporting(true);
    try {
      const now = new Date().toISOString();
      for (const setId of selectedIds) {
        const setData = sets.find(s => s.id === setId);
        if (!setData) continue;

        const tuneEntries = [];

        if (sourceUid) {
          // Firestore source: copy tunes one-by-one (same as social import)
          const tuneRefs = setData.tunes || [];
          const idMap = {};
          for (const entry of tuneRefs) {
            const srcId = entry.id || entry.tuneId;
            if (!srcId || idMap[srcId]) continue;
            try {
              const tuneSnap = await getDoc(doc(db, 'users', sourceUid, 'tunes', srcId));
              if (!tuneSnap.exists()) continue;
              const newTuneId = crypto.randomUUID();
              await setDoc(doc(db, 'users', user.uid, 'tunes', newTuneId), {
                ...tuneSnap.data(),
                id: newTuneId,
                isSample: true,
                createdAt: now,
                updatedAt: now,
                lastViewedAt: '',
                eventDate: '',
              });
              idMap[srcId] = newTuneId;
            } catch (_) {}
          }
          tuneRefs.forEach(e => {
            const srcId = e.id || e.tuneId;
            if (idMap[srcId]) tuneEntries.push({ id: idMap[srcId], title: e.title || '' });
          });
          const { id: _id, _sourceUid: _s, tunes: _t, ...setBody } = setData;
          const newSetId = crypto.randomUUID();
          await setDoc(doc(db, 'users', user.uid, 'sets', newSetId), {
            ...setBody, id: newSetId, tunes: tuneEntries, createdAt: now, updatedAt: now,
          });
        } else {
          // Static fallback
          for (const tuneTemplate of setData.tunes) {
            const newTuneId = crypto.randomUUID();
            await setDoc(doc(db, 'users', user.uid, 'tunes', newTuneId), {
              ...tuneTemplate, id: newTuneId, createdAt: now, updatedAt: now, lastViewedAt: '', eventDate: '',
            });
            tuneEntries.push({ tuneId: newTuneId, title: tuneTemplate.title });
          }
          const { id: _id, tunes: _t, ...setBody } = setData;
          const newSetId = crypto.randomUUID();
          await setDoc(doc(db, 'users', user.uid, 'sets', newSetId), {
            ...setBody, id: newSetId, tunes: tuneEntries, createdAt: now, updatedAt: now,
          });
        }
      }
      setDone(true);
      setTimeout(onNext, 900);
    } catch (err) {
      console.error('Sample import error', err);
      onNext();
    } finally {
      setImporting(false);
    }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '2rem 0' }}>
      <p style={{ opacity: 0.6 }}>Loading starter sets…</p>
    </div>
  );

  if (done) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem 0' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
        <p style={{ fontWeight: 700 }}>Starter sets added to your library!</p>
      </div>
    );
  }

  const genreLabel = selectedGenres.filter(g => g !== 'Other').slice(0, 2).join(' & ') || 'your genre';

  return (
    <>
      <h2 style={{ fontSize: '1.2em', margin: '0 0 0.4rem', fontWeight: 800 }}>
        Start with some ready-made sets?
      </h2>
      <p style={{ margin: '0 0 1rem', fontSize: '0.88rem', opacity: 0.7 }}>
        We've put together some starter {genreLabel} sets — select any you'd like added to your library.
      </p>

      <div style={{ textAlign: 'left', display: 'grid', gap: '8px', marginBottom: '1.2rem' }}>
        {sets.map(s => (
          <label key={s.id} style={{
            display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer',
            padding: '9px 12px', borderRadius: '8px',
            background: selectedIds.includes(s.id) ? 'color-mix(in srgb, var(--text-link, #228be6) 10%, transparent)' : 'transparent',
            border: `1px solid ${selectedIds.includes(s.id) ? 'color-mix(in srgb, var(--text-link, #228be6) 40%, var(--border-color) 60%)' : 'var(--border-color, #ccc)'}`,
          }}>
            <input
              type="checkbox"
              checked={selectedIds.includes(s.id)}
              onChange={() => toggleSet(s.id)}
              style={{ width: '17px', height: '17px', marginTop: '1px', cursor: 'pointer', accentColor: 'var(--text-link, #228be6)' }}
            />
            <div>
              <div style={{ fontWeight: 600 }}>{s.title || s.name || 'Untitled Set'}</div>
              {s.description && <div style={{ fontSize: '0.8rem', opacity: 0.65, marginTop: '2px' }}>{s.description}</div>}
              {(s.tunes?.length > 0) && <div style={{ fontSize: '0.78rem', opacity: 0.5, marginTop: '2px' }}>{s.tunes.length} tune{s.tunes.length !== 1 ? 's' : ''}</div>}
            </div>
          </label>
        ))}
      </div>

      <button
        style={{ ...btnPrimary, width: '100%', marginBottom: '0.75rem', opacity: importing ? 0.6 : 1 }}
        onClick={handleImport}
        disabled={importing}
      >
        {importing ? 'Importing…' : selectedIds.length > 0 ? `Add ${selectedIds.length} Set${selectedIds.length !== 1 ? 's' : ''} →` : 'Continue →'}
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button style={btnSecondary} onClick={onBack}>Back</button>
        <button style={btnSkip} onClick={onSkip}>Skip setup</button>
      </div>
    </>
  );
}

// ─── Step 3: Complexity ───────────────────────────────────────────────
function ComplexityStep({ mode, complexity, setComplexity, onNext, onBack, onSkip }) {
  const isNotes = mode === 'notes';
  const isBoth = mode === 'all';

  const simplePoints = isNotes ? [
    'Add notes, search, organize with tags',
    'Basic status and tag filters',
    'Everything you need for your note library',
  ] : isBoth ? [
    'Add tunes and notes, search, make sets',
    'Basic genre / type / status filters',
    'Everything you need to build your library',
  ] : [
    'Add tunes, search, make sets',
    'Basic genre / type / status filters',
    'Everything you need to build your library',
  ];

  const fullPoints = isNotes ? [
    '+ Sessions — track your practice or writing sessions',
    '+ Social — share notes with others',
    '+ Folders for organizing collections',
    '+ Advanced search: tags, ratings, deep filtering',
  ] : isBoth ? [
    '+ Sessions — track your playing history',
    '+ Social — share tunes and notes with other musicians',
    '+ Program templates for gigs & events',
    '+ Advanced search: key, rating, composer, deep tagging',
  ] : [
    '+ Sessions — track your playing history',
    '+ Social — share tunes with other musicians',
    '+ Program templates for gigs & events',
    '+ Advanced search: key, rating, composer, deep tagging',
  ];

  const optionCard = (id, title, points, color) => (
    <div
      onClick={() => setComplexity(id)}
      style={{
        padding: '14px 14px 12px', borderRadius: '12px',
        border: `2px solid ${complexity === id ? color : 'var(--border-color, #ccc)'}`,
        background: complexity === id ? `color-mix(in srgb, ${color} 12%, transparent)` : 'transparent',
        cursor: 'pointer', textAlign: 'left',
        transition: 'border-color 0.15s',
        marginBottom: '10px',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: '0.4rem' }}>{title}</div>
      <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.85rem', opacity: 0.85, lineHeight: 1.6 }}>
        {points.map((p, i) => <li key={i}>{p}</li>)}
      </ul>
    </div>
  );

  return (
    <>
      <h2 style={{ fontSize: '1.2em', margin: '0 0 0.4rem', fontWeight: 800 }}>
        How much do you want to start with?
      </h2>
      <p style={{ margin: '0 0 1rem', fontSize: '0.88rem', opacity: 0.7 }}>
        You can unlock more anytime from Settings or by tapping locked items.
      </p>

      {optionCard('simple', 'Start Simple', simplePoints, '#16a34a')}
      {optionCard('full', 'Full Access', fullPoints, '#228be6')}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.4rem' }}>
        <button style={btnSkip} onClick={onSkip}>Skip setup</button>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={btnSecondary} onClick={onBack}>Back</button>
          <button style={btnPrimary} onClick={onNext}>Next</button>
        </div>
      </div>
    </>
  );
}

// ─── Step 4: CTA ──────────────────────────────────────────────────────
function CtaStep({ mode, genres, complexity, onFinish, onBack }) {
  const modeLabel = mode === 'notes' ? 'Notes' : mode === 'all' ? 'Tunes + Notes' : 'Tunes';
  const genreStr = genres.filter(Boolean).slice(0, 2).join(' & ') || 'your genre';
  const complexStr = complexity === 'simple' ? 'simple setup' : 'full access';

  return (
    <>
      <div style={{ fontSize: '2.2rem', marginBottom: '0.5rem' }}>✅</div>
      <h2 style={{ fontSize: '1.25em', margin: '0 0 0.6rem', fontWeight: 800 }}>
        You're all set!
      </h2>
      <p style={{ margin: '0 0 1.4rem', lineHeight: 1.6, opacity: 0.9 }}>
        {mode !== 'notes'
          ? <>Set up for <strong>{genreStr}</strong> in <strong>{modeLabel}</strong> mode with <strong>{complexStr}</strong>.</>
          : <>Set up in <strong>{modeLabel}</strong> mode with <strong>{complexStr}</strong>.</>
        }
      </p>

      <button style={{ ...btnPrimary, width: '100%', padding: '12px', fontSize: '1rem', marginBottom: '0.75rem' }} onClick={() => onFinish(true)}>
        {mode === 'notes' ? 'Add Your First Note →' : mode === 'all' ? 'Add Your First Tune or Note →' : 'Add Your First Tune →'}
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button style={btnSecondary} onClick={onBack}>Back</button>
        <button style={{ ...btnSkip, fontSize: '0.88rem' }} onClick={() => onFinish(false)}>
          Take me to my library
        </button>
      </div>
    </>
  );
}

// ─── Main wizard ──────────────────────────────────────────────────────
export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { prefs, patchPrefs } = useUserPrefs();

  const [step, setStep] = useState(0);
  const [chosenMode, setChosenMode] = useState('tunes');
  const [selectedGenres, setSelectedGenres] = useState(['Irish']);
  const [otherGenre, setOtherGenre] = useState('');
  const [chosenComplexity, setChosenComplexity] = useState('simple');

  // Steps depend on mode (genres + samples only for tunes/all)
  const needsGenres = chosenMode !== 'notes';
  const steps = [
    'welcome', 'mode',
    ...(needsGenres ? ['genres', 'samples'] : []),
    'complexity', 'cta',
  ];
  const currentStep = steps[step];

  const goNext = () => setStep(s => Math.min(s + 1, steps.length - 1));
  const goBack = () => setStep(s => Math.max(s - 1, 0));

  const handleSkip = async () => {
    await patchPrefs({
      tutorialDismissed: true,
      onboardingComplete: true,
      complexityLevel: 'full',
    });
  };

  const handleFinish = async (goToAdd) => {
    const genreList = [
      ...selectedGenres.filter(g => g !== 'Other'),
      ...(otherGenre.trim() ? [otherGenre.trim()] : []),
    ];
    const finalGenres = genreList.length > 0 ? genreList : ['Irish'];

    await patchPrefs({
      appMode: chosenMode,
      customGenres: {
        ...(prefs?.customGenres || {}),
        tunes: finalGenres,
      },
      defaultGenreByMode: {
        ...(prefs?.defaultGenreByMode || {}),
        tunes: finalGenres[0] || 'Irish',
      },
      complexityLevel: chosenComplexity,
      tutorialDismissed: true,
      onboardingComplete: true,
    });

    if (goToAdd) {
      navigate(chosenMode === 'notes' ? '/add?mode=notes' : '/add');
    }
  };

  return (
    <div style={overlay}>
      <div style={card}>
        {currentStep === 'welcome' && <WelcomeStep onNext={goNext} onSkip={handleSkip} />}
        {currentStep === 'mode' && (
          <ModeStep mode={chosenMode} setMode={setChosenMode} onNext={goNext} onBack={goBack} onSkip={handleSkip} />
        )}
        {currentStep === 'genres' && (
          <GenresStep
            selected={selectedGenres} setSelected={setSelectedGenres}
            otherGenre={otherGenre} setOtherGenre={setOtherGenre}
            onNext={goNext} onBack={goBack} onSkip={handleSkip}
          />
        )}
        {currentStep === 'samples' && (
          <SamplesStep
            selectedGenres={selectedGenres.filter(g => g !== 'Other')}
            onNext={goNext} onBack={goBack} onSkip={handleSkip}
          />
        )}
        {currentStep === 'complexity' && (
          <ComplexityStep mode={chosenMode} complexity={chosenComplexity} setComplexity={setChosenComplexity} onNext={goNext} onBack={goBack} onSkip={handleSkip} />
        )}
        {currentStep === 'cta' && (
          <CtaStep
            mode={chosenMode}
            genres={[...selectedGenres.filter(g => g !== 'Other'), ...(otherGenre ? [otherGenre] : [])]}
            complexity={chosenComplexity}
            onFinish={handleFinish}
            onBack={goBack}
          />
        )}
        <Dots steps={steps} current={step} />
      </div>
    </div>
  );
}
